import { Link, useLocation } from "wouter";
import { 
  LayoutDashboard, 
  FileSpreadsheet, 
  Package2, 
  Settings, 
  LogOut,
  User,
  History,
  Users,
  Calendar,
  ShoppingCart,
  Warehouse,
  FileText,
  Truck
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "@/assets/logo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { useSelector } from "react-redux";

export enum UserRole {
  Manager = 'Manager',
  Planning = 'Planning Team',
  Appointment = 'Appointment Team',
  Sales_Order = 'Sales Order Team',
  Courier = 'Courier Team',
  Invoicing = 'Invoicing Team',
  Warehouse = 'Warehouse Team',
}

const roleToHrefMap: Record<string, string> = {
  [UserRole.Manager]: 'manager',
  [UserRole.Planning]: 'planner',
  [UserRole.Appointment]: 'appointment',
  [UserRole.Sales_Order]: 'sales-order',
  [UserRole.Courier]: 'courier',
  [UserRole.Invoicing]: 'invoice',
  [UserRole.Warehouse]: 'warehouse',
};

export const Sidebar = () => {
  const [location, setLocation] = useLocation();
  const user = useSelector((state: any) => state.user.email);
  const role = useSelector((state: any) => state.user.role);

  const navItems = [
    { href: "/dashboard", label: "Dashboard", icon: <LayoutDashboard className="mr-3 h-5 w-5" /> },
    // { href: "/po-validation", label: "PO Validation", icon: <Package2 className="mr-3 h-5 w-5" /> },
    // { href: "/po-management", label: "PO Management", icon: <FileSpreadsheet className="mr-3 h-5 w-5" /> },
  ];

  const bottomNavItems = [
    { href: "/audit-logs", label: "Audit Logs", icon: <History className="mr-3 h-5 w-5" /> },
    { href: "/settings", label: "Settings", icon: <Settings className="mr-3 h-5 w-5" /> },
  ];

  const roleBasedNavItems = [
    { href: "/role/manager", label: "Manager", icon: <Users className="mr-3 h-5 w-5" /> },
    { href: "/role/planner", label: "Planner", icon: <FileSpreadsheet className="mr-3 h-5 w-5" /> },
    { href: "/role/appointment", label: "Appointment", icon: <Calendar className="mr-3 h-5 w-5" /> },
    { href: "/role/sales-order", label: "Sales Order", icon: <ShoppingCart className="mr-3 h-5 w-5" /> },
    { href: "/role/warehouse", label: "Warehouse", icon: <Warehouse className="mr-3 h-5 w-5" /> },
    { href: "/role/invoice", label: "Invoice", icon: <FileText className="mr-3 h-5 w-5" /> },
    { href: "/role/courier", label: "Courier", icon: <Truck className="mr-3 h-5 w-5" /> },
  ];

  // Determine which role-based nav items to show
  const visibleNavItems = role === UserRole.Manager
    ? roleBasedNavItems
    : roleBasedNavItems.filter(
        (item) => item.href === `/role/${roleToHrefMap[role]}`
      );

  return (
    <div className="hidden md:flex md:flex-shrink-0">
      <div className="flex flex-col w-64 bg-white border-r border-gray-200">
        {/* Logo area */}
        <div className="flex items-center justify-center h-24 px-2">
          <div className="flex items-center w-full justify-center">
            <Logo className="h-16 w-full max-w-[200px]" />
          </div>
        </div>
        
        {/* Main navigation */}
        <div className="flex flex-col flex-grow overflow-y-auto">
          <div className="px-3 py-4">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
              Main
            </h2>
            <nav className="space-y-1">
              {navItems.map((item) => (
                <Link 
                  key={item.href} 
                  href={item.href}
                  className={cn(
                    "flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors",
                    location === item.href
                      ? "bg-purple-50 text-primary font-medium"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                >
                  <span className={cn(
                    "mr-3 h-5 w-5",
                    location === item.href ? "text-primary" : "text-gray-500"
                  )}>
                    {item.icon}
                  </span>
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="px-3 py-4">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
              PO Management
            </h2>
            <nav className="space-y-1">
              {visibleNavItems.map((item) => {
                const isActive = location === item.href;
                return (
                  <button
                    key={item.href}
                    onClick={() => setLocation(item.href)}
                    className={cn(
                      "w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors text-left",
                      isActive
                        ? "bg-purple-50 text-primary font-medium"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    )}
                  >
                    <span className={cn(
                      "mr-3 h-5 w-5",
                      isActive ? "text-primary" : "text-gray-500"
                    )}>
                      {item.icon}
                    </span>
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="mt-auto px-3 py-4">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
              Settings
            </h2>
            <nav className="space-y-1">
              {bottomNavItems.map((item) => (
                <Link 
                  key={item.href} 
                  href={item.href}
                  className={cn(
                    "flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors",
                    location === item.href
                      ? "bg-purple-50 text-primary font-medium"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                >
                  <span className={cn(
                    "mr-3 h-5 w-5",
                    location === item.href ? "text-primary" : "text-gray-500"
                  )}>
                    {item.icon}
                  </span>
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
        
        {/* User profile */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center">
            <Avatar className="h-10 w-10">
              <AvatarImage src="https://cdn-icons-png.flaticon.com/512/7915/7915522.png" alt="User avatar" />
              <AvatarFallback>AP</AvatarFallback>
            </Avatar>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">{user}</p>
              <p className="text-xs font-medium text-gray-500">{role}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
